import {
  ArchiveIcon,
  Avatar,
  BookmarkIcon,
  cn,
  focusRing,
  LayersIcon,
  MessageIcon,
  type NavItem,
  NavRail,
  PhoneIcon,
  RailSlot,
  SearchIcon,
  StarIcon,
  TabBar,
  UiLink,
  UsersIcon,
} from "@rhema/ui";
import { type ReactNode, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { BackupPrompts } from "../backup/MessageBackup";
import { CallProvider, useCalls } from "../calls/CallProvider";
import { useFlushDrafts } from "../conversation/drafts";
import { isMuted, unreadCount } from "../data/view";
import { ImportBanner } from "../import/ImportBanner";
import { ImportProvider } from "../import/ImportProvider";
import { FeedbackProvider } from "../menus/Feedback";
import { useInAppNotifications, useUnreadTitle } from "../notifications/useNotifications";
import { QueravelPanelProvider, useQueravelPanel } from "../queravel/panel";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { QueravelPanel } from "../queravel/QueravelPanel";
import { QueravelTile } from "../queravel/QueravelParts";
import { QueravelProvider, useQueravelWriting } from "../queravel/useQueravel";
import { useClient, useClientState, useMe } from "../session/session";
import { spaceActivity } from "../spaces/data";
import { SpaceTiles } from "../spaces/SpaceViews";
import { useIsDesktop } from "./media";

function sectionOf(pathname: string) {
  const first = pathname.split("/")[1] ?? "";
  return first === "" ? "chats" : first;
}

// Top level screens show the tab bar on phones. Detail screens take the full height.
const TAB_ROOTS = new Set(["/chats", "/spaces", "/calls", "/contacts", "/you"]);

export function AppShell() {
  // Calls live above the screens, so a call keeps going while you move between chats.
  return (
    <CallProvider>
      <QueravelProvider>
        <QueravelPanelProvider>
          <FeedbackProvider>
            <ImportProvider>
              <Shell />
              <BackupPrompts />
            </ImportProvider>
          </FeedbackProvider>
        </QueravelPanelProvider>
      </QueravelProvider>
    </CallProvider>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  const state = useClientState();
  // Channels count in their space's tile, not in Chats.
  const unreadTotal = Object.values(state.chats).reduce(
    (n, c) => n + (isMuted(c) || c.archived || c.kind === "channel" ? 0 : unreadCount(c)),
    0,
  );
  const { unseenMissed } = useCalls();
  const spaceMentions = state.spaces.reduce((n, s) => n + spaceActivity(state, s.id).mentions, 0);
  const me = useMe();
  const section = sectionOf(pathname);
  const queravel = useQueravelOffered();
  const queravelWriting = useQueravelWriting();
  const queravelPanel = useQueravelPanel();
  useUnreadTitle(unreadTotal);
  useInAppNotifications();
  useFlushDrafts();
  const navigate = useNavigate();
  const client = useClient();
  // Reminders load once, so the Later badge can count the ones that are due.
  useEffect(() => {
    void client.loadReminders().catch(() => undefined);
  }, [client]);
  const laterDue = (state.reminders ?? []).filter((r) => new Date(r.remindAt).getTime() <= Date.now()).length;

  // ⌘K or Ctrl+K opens search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // In the message box ⌘K adds a link, and the box has already taken the key.
      if (e.defaultPrevented) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        navigate("/search");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (desktop) {
    const items: NavItem[] = [
      {
        id: "chats",
        label: "Chats",
        href: "/chats",
        icon: <MessageIcon size={21} strokeWidth={2.2} />,
        badge: { count: unreadTotal },
      },
      {
        id: "calls",
        label: "Calls",
        href: "/calls",
        icon: <PhoneIcon size={20} />,
        badge: { count: unseenMissed, variant: "missed" },
      },
      { id: "contacts", label: "Contacts", href: "/contacts", icon: <UsersIcon size={21} /> },
      { id: "search", label: "Search", href: "/search", icon: <SearchIcon size={20} /> },
      {
        id: "later",
        label: "Later",
        href: "/later",
        icon: <BookmarkIcon size={20} />,
        badge: { count: laterDue },
      },
      {
        id: "starred",
        label: "Starred messages",
        href: "/chats?filter=favourites",
        icon: <StarIcon size={20} />,
      },
      {
        id: "archive",
        label: "Archived chats",
        href: "/chats?filter=archived",
        icon: <ArchiveIcon size={20} />,
      },
    ];
    return (
      <div className="flex h-dvh min-h-0 overflow-hidden bg-linen">
        <NavRail
          items={items}
          activeId={section}
          homeHref="/chats"
          after={
            queravel && (
              <RailSlot active={queravelPanel.open} tone="queravel">
                <UiLink
                  href="/queravel"
                  onClick={(e) => {
                    e.preventDefault();
                    queravelPanel.toggle();
                  }}
                  aria-label="Queravel"
                  aria-current={queravelPanel.open ? "true" : undefined}
                  title="Queravel"
                  className={cn(
                    "relative flex h-12 w-12 items-center justify-center rounded-tile",
                    queravelPanel.open ? "bg-queravel-tint" : "hover:bg-line",
                    focusRing,
                  )}
                >
                  <QueravelTile size={30} />
                  {queravelWriting && (
                    <span
                      role="img"
                      aria-label="Queravel is writing"
                      className="absolute -top-0.5 -right-0.5 h-3 w-3 animate-pulse rounded-full border-2 border-surface-header bg-queravel-accent"
                    />
                  )}
                </UiLink>
              </RailSlot>
            )
          }
          extra={<SpaceTiles activeId={section === "spaces" ? pathname.split("/")[2] : undefined} />}
          bottom={
            <>
              <UiLink
                href="/you"
                aria-label="Settings"
                aria-current={section === "you" ? "page" : undefined}
                className={cn(
                  "rounded-full p-[3px] ring-2 transition-colors",
                  section === "you" ? "ring-blue" : "ring-transparent hover:ring-line",
                  focusRing,
                )}
              >
                <Avatar name={me.display_name} src={me.avatar_url} tone="self" size={40} />
              </UiLink>
            </>
          }
        />
        <Outlet />
        {pathname !== "/import" && <ImportBanner variant="desktop" />}
      </div>
    );
  }

  const tabs: NavItem[] = [
    {
      id: "chats",
      label: "Chats",
      href: "/chats",
      icon: <MessageIcon size={22} strokeWidth={section === "chats" ? 2.2 : 2} />,
      badge: { count: unreadTotal },
    },
    {
      id: "calls",
      label: "Calls",
      href: "/calls",
      icon: <PhoneIcon size={21} strokeWidth={section === "calls" ? 2.2 : 2} />,
      badge: { count: unseenMissed, variant: "missed" },
    },
    {
      id: "spaces",
      label: "Spaces",
      href: "/spaces",
      icon: <LayersIcon size={21} strokeWidth={section === "spaces" ? 2.2 : 2} />,
      badge: { count: spaceMentions },
    },
    { id: "contacts", label: "Contacts", href: "/contacts", icon: <UsersIcon size={22} /> },
    {
      id: "you",
      label: "Settings",
      href: "/you",
      icon: <Avatar name={me.display_name} src={me.avatar_url} tone="self" size={26} />,
    },
  ];

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-linen">
      <div className="relative flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
      {TAB_ROOTS.has(pathname) && <ImportBanner variant="phone" />}
      {TAB_ROOTS.has(pathname) && <TabBar items={tabs} activeId={section} />}
    </div>
  );
}

// Desktop columns beside the rail: a list, the main view and an optional side panel. While the
// Queravel panel is open it takes the side panel's place, and closing it brings the other back.
export function Panels({ list, main, aside }: { list?: ReactNode; main: ReactNode; aside?: ReactNode }) {
  const queravel = useQueravelPanel().open;
  return (
    <>
      {list && (
        <section
          aria-label="List"
          className="flex h-full w-[350px] flex-none flex-col border-r border-line bg-linen max-[1100px]:w-[300px]"
        >
          {list}
        </section>
      )}
      <main className="relative flex h-full min-w-0 flex-1 flex-col bg-chat">{main}</main>
      {queravel ? (
        <aside
          aria-label="Queravel panel"
          className="flex h-full w-[420px] flex-none flex-col border-l border-line bg-surface max-[1200px]:w-[360px]"
        >
          <QueravelPanel variant="aside" />
        </aside>
      ) : (
        aside && (
          <aside className="flex h-full w-[340px] flex-none flex-col overflow-y-auto border-l border-line bg-linen max-[1200px]:w-[300px]">
            {aside}
          </aside>
        )
      )}
    </>
  );
}

// Phone screens fill the space above the tab bar.
export function PhoneScreen({ children, className }: { children: ReactNode; className?: string }) {
  return <main className={cn("relative flex min-h-0 flex-1 flex-col", className)}>{children}</main>;
}
